import React, {
  useState, useRef, useContext, createContext,
} from 'react';
import { Paper, Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// ให้ header ของ modal (เขียนใน component ผู้เรียก ไม่ใช่ในไฟล์นี้) อ่าน/toggle
// minimized ได้ — ต้องส่งผ่าน Context เท่านั้น (ไม่ใช่ prop ตรงเข้า PaperComponent)
// เพราะ Dialog ต้องได้ PaperComponent เป็น reference เดิมทุก render ไม่งั้น React
// unmount ลูกทั้งก้อนทุกครั้งที่ minimized เปลี่ยน (state ข้างในหายหมด)
export const MinimizedContext = createContext(false);

// เหมือนกัน แต่สำหรับสถานะ "ขยายเต็มจอ" ซึ่งจัดการอยู่ภายใน WindowedPaper เอง (ผูกกับ
// ตำแหน่ง/ขนาดของ Paper โดยตรง) — header ภายนอกแค่อ่านค่า/เรียก toggle ผ่าน context นี้
export const WindowedControlsContext = createContext({ maximized: false, toggleMaximize: () => {} });

const EDGE = 8; // ความหนา hotzone ขอบ (บน/ล่าง/ซ้าย/ขวา)
const CORNER = 16; // ขนาด hotzone มุม (สี่เหลี่ยมจัตุรัส)

const CURSOR = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

const HANDLE_SX = {
  n: { top: -EDGE / 2, left: CORNER, right: CORNER, height: EDGE },
  s: { bottom: -EDGE / 2, left: CORNER, right: CORNER, height: EDGE },
  e: { right: -EDGE / 2, top: CORNER, bottom: CORNER, width: EDGE },
  w: { left: -EDGE / 2, top: CORNER, bottom: CORNER, width: EDGE },
  ne: { top: -CORNER / 2, right: -CORNER / 2, width: CORNER, height: CORNER },
  nw: { top: -CORNER / 2, left: -CORNER / 2, width: CORNER, height: CORNER },
  se: { bottom: -CORNER / 2, right: -CORNER / 2, width: CORNER, height: CORNER },
  sw: { bottom: -CORNER / 2, left: -CORNER / 2, width: CORNER, height: CORNER },
};

const DIRECTIONS = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const DEFAULT_MINIMIZED_SIZE = { width: 320, height: 52 };

// สร้าง PaperComponent สำหรับ MUI Dialog แบบ "หน้าต่าง Windows" — ลากย้ายตำแหน่งได้
// (แถบเทาบางด้านบน), ปรับขนาดได้ทุกทิศทาง (ขอบ 4 + มุม 4), ขยายเต็มจอ/ย่อกลับได้
// เรียกฟังก์ชันนี้ "ครั้งเดียวระดับ module" ต่อการใช้งานแต่ละจุด (เก็บผลลัพธ์ไว้เป็น
// ค่าคงที่ระดับไฟล์) ห้ามเรียกซ้ำใน render — Dialog ต้องได้ PaperComponent reference
// เดิมเสมอ ไม่งั้น React unmount เนื้อหาข้างในทิ้งทุกครั้งที่ re-render
export function createWindowedPaper({ defaultSize, minSize, minimizedSize = DEFAULT_MINIMIZED_SIZE }) {
  return function WindowedPaper(props) {
    const minimized = useContext(MinimizedContext);
    const posRef = useRef({ x: 0, y: 0 });
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const sizeRef = useRef(defaultSize);
    const [size, setSize] = useState(defaultSize);
    const [maximized, setMaximized] = useState(false);
    const preMaxRef = useRef(null);

    const toggleMaximize = () => {
      setMaximized((m) => {
        if (!m) {
          preMaxRef.current = { pos: posRef.current, size: sizeRef.current };
        } else if (preMaxRef.current) {
          posRef.current = preMaxRef.current.pos;
          sizeRef.current = preMaxRef.current.size;
          setPos(preMaxRef.current.pos);
          setSize(preMaxRef.current.size);
        }
        return !m;
      });
    };

    const handleDragStart = (e) => {
      if (e.button !== 0 || maximized) return;
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const origin = posRef.current;
      const onMove = (ev) => {
        const next = { x: origin.x + (ev.clientX - startX), y: origin.y + (ev.clientY - startY) };
        posRef.current = next;
        setPos(next);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    // ทิศทางเดียวกันหมดทุกขอบ/มุม — 'e'/'w' คุม width, 'n'/'s' คุม height, มุมคุมทั้งคู่
    // ทิศ w/n ต้องขยับตำแหน่งตามด้วย (อิงจากขนาดที่ clamp แล้ว) ให้ขอบตรงข้ามอยู่นิ่ง
    const handleResizeStart = (direction) => (e) => {
      if (e.button !== 0 || maximized) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const originSize = sizeRef.current;
      const originPos = posRef.current;
      const maxWidth = window.innerWidth - 40;
      const maxHeight = window.innerHeight - 80;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let { width, height } = originSize;

        if (direction.includes('e')) width = originSize.width + dx;
        if (direction.includes('w')) width = originSize.width - dx;
        if (direction.includes('s')) height = originSize.height + dy;
        if (direction.includes('n')) height = originSize.height - dy;

        width = Math.max(minSize.width, Math.min(maxWidth, width));
        height = Math.max(minSize.height, Math.min(maxHeight, height));

        let { x, y } = originPos;
        if (direction.includes('w')) x = originPos.x + (originSize.width - width);
        if (direction.includes('n')) y = originPos.y + (originSize.height - height);

        sizeRef.current = { width, height };
        posRef.current = { x, y };
        setSize({ width, height });
        setPos({ x, y });
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    const effectiveSize = minimized ? minimizedSize : size;

    return (
      <Paper
        {...props}
        style={{
          ...props.style,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(maximized
            ? { width: 'calc(100vw - 16px)', height: 'calc(100vh - 16px)', transform: 'translate(0px, 0px)' }
            : { width: effectiveSize.width, height: effectiveSize.height, transform: `translate(${pos.x}px, ${pos.y}px)` }),
        }}
      >
        <Box
          onMouseDown={handleDragStart}
          sx={{
            height: 14,
            bgcolor: '#cbd5e1',
            cursor: maximized ? 'default' : 'move',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { bgcolor: maximized ? '#cbd5e1' : '#94a3b8' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 14, color: '#64748b' }} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <WindowedControlsContext.Provider value={{ maximized, toggleMaximize }}>
            {props.children}
          </WindowedControlsContext.Provider>
        </Box>
        {!minimized && !maximized && DIRECTIONS.map((dir) => (
          <Box
            key={dir}
            onMouseDown={handleResizeStart(dir)}
            sx={{ position: 'absolute', cursor: CURSOR[dir], zIndex: dir.length === 2 ? 2 : 1, ...HANDLE_SX[dir] }}
          />
        ))}
      </Paper>
    );
  };
}

export default createWindowedPaper;
